// Committed-composition replay — the browser half of the composition proof.
//
// Mirrors apps/racing/services/composition.py: field, environment, distance
// and WEIGHTS all derive from PUBLIC data (the epoch commitment hash + the
// published roster snapshot), so this module lets any player recompute the
// entire epoch's composition and check the racecard against it. The epoch
// commitment is part of the proof surface — clients verify COMPOSITION, not
// just derivation.

import { StreamGenerator } from '../static/js/fairness.js';

export const DISTANCE_DOMAIN = 'world-distance:v1';
export const STAGE_DOMAIN = 'world-stage:v1';
export const WEATHER_DOMAIN = 'world-weather:v1';
export const FIELD_DOMAIN = 'world-field:v1';
export const AUGMENT_DOMAIN = 'world-augment:v1';

const AUGMENT_MULT_MIN = 0.90;
const AUGMENT_MULT_MAX = 1.10;

const APTITUDE_SPREAD = 0.30;
const APTITUDE_SIGMA_M = 250.0;
const BASE_MIN = 12.0;
const BASE_MAX = 35.0;
const FORM_JITTER = 1.5;
const CAREER_DAYS = 90;
const PEAK_DAYS = 30;
const END_QUALITY = 0.25;
const STRENGTH_LO = 1.0 * END_QUALITY * 0.92;
const STRENGTH_HI = 3.0 * 1.0 * 1.08;
const SILKS = [
  '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4',
  '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff',
];

function fade(ageDays) {
  if (ageDays <= PEAK_DAYS) return 1.0;
  const span = Math.max(1.0, CAREER_DAYS - PEAK_DAYS);
  return Math.max(END_QUALITY, 1.0 - (1.0 - END_QUALITY) * (ageDays - PEAK_DAYS) / span);
}

export function aptitudeMultiplier(distanceM, muM, sigmaM = APTITUDE_SIGMA_M) {
  const z = (distanceM - muM) / sigmaM;
  return 1.0 - APTITUDE_SPREAD / 2 + APTITUDE_SPREAD * Math.exp(-(z * z) / 2);
}

export function deriveWeight(snapshotHorse, distanceM, stageId, formJitter, augmentMult = 1.0) {
  const affinity = Number(snapshotHorse.affinities?.[stageId] ?? 1.0);
  const strength = Number(snapshotHorse.quality) * fade(Number(snapshotHorse.ageDays)) * affinity;
  let norm = (strength - STRENGTH_LO) / (STRENGTH_HI - STRENGTH_LO);
  norm = Math.max(0, Math.min(1, norm));
  let base = BASE_MIN + (BASE_MAX - BASE_MIN) * norm;
  base = Math.min(BASE_MAX, Math.max(BASE_MIN, base + formJitter));
  if (augmentMult !== 1.0) {
    base = Math.min(BASE_MAX, Math.max(BASE_MIN, base * augmentMult));
  }
  const multiplier = aptitudeMultiplier(distanceM, Number(snapshotHorse.mu), Number(snapshotHorse.sigma ?? APTITUDE_SIGMA_M));
  // Python round() is banker's rounding — mirror it exactly.
  const weight = bankersRound(base * multiplier);
  return Math.max(10, Math.min(40, weight));
}

// Mirrors composition.evaluate_augments: fixed trigger order (jockey then
// trainer, snapshot order), chance triggers drawing one float each from the
// AUGMENT_DOMAIN stream keyed by slot:gate. Replayable from the published
// epoch snapshot — the client verifies the fold, not just the base weights.
export async function evaluateAugments(commitmentHash, slot, gate, snapshotHorse, stageId, weatherId, distanceM) {
  const tack = snapshotHorse.tack;
  if (!tack) return 1.0;
  const gen = new StreamGenerator(commitmentHash, AUGMENT_DOMAIN, `${slot}:${gate}`);
  let multiplier = 1.0;
  for (const role of ['jockey', 'trainer']) {
    const entity = tack[role];
    if (!entity) continue;
    for (const augment of entity.augments ?? []) {
      const value = String(augment.triggerValue);
      let triggered;
      if (augment.triggerKind === 'weather') triggered = value === weatherId;
      else if (augment.triggerKind === 'stage') triggered = value === stageId;
      else if (augment.triggerKind === 'distance') triggered = Number(value) === distanceM;
      else triggered = (await gen.generateFloat()) < Number(value) / 10000.0;
      if (triggered) multiplier *= augment.boostBp / 10000.0;
    }
  }
  return Math.min(AUGMENT_MULT_MAX, Math.max(AUGMENT_MULT_MIN, multiplier));
}

function bankersRound(x) {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff > 0.5) return floor + 1;
  if (diff < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

export async function deriveDistance(commitmentHash, slot, distanceMix) {
  const gen = new StreamGenerator(commitmentHash, DISTANCE_DOMAIN, String(slot));
  const f = await gen.generateFloat();
  const items = Object.entries(distanceMix).sort((a, b) => Number(a[0]) - Number(b[0]));
  const total = items.reduce((sum, [, w]) => sum + w, 0);
  let acc = 0;
  for (const [meters, weight] of items) {
    acc += weight / total;
    if (f < acc) return Number(meters);
  }
  return Number(items[items.length - 1][0]);
}

export async function deriveEnvironment(commitmentHash, slot, stageIds, weatherIds) {
  const sgen = new StreamGenerator(commitmentHash, STAGE_DOMAIN, String(slot));
  const wgen = new StreamGenerator(commitmentHash, WEATHER_DOMAIN, String(slot));
  const stage = stageIds[await sgen.generateInt(0, stageIds.length - 1)];
  const weather = weatherIds[await wgen.generateInt(0, weatherIds.length - 1)];
  return [stage, weather];
}

export async function composeSlot(commitmentHash, slot, roster, { distanceMix, stageIds, weatherIds, fieldSize }) {
  const distanceM = await deriveDistance(commitmentHash, slot, distanceMix);
  const [stageId, weatherId] = await deriveEnvironment(commitmentHash, slot, stageIds, weatherIds);
  const gen = new StreamGenerator(commitmentHash, FIELD_DOMAIN, String(slot));
  const pool = [...roster.keys()];
  const silkPool = [...SILKS];
  const runners = [];
  for (let gate = 0; gate < fieldSize; gate++) {
    const pick = pool.splice(await gen.generateInt(0, pool.length - 1), 1)[0];
    const silk = silkPool.splice(await gen.generateInt(0, silkPool.length - 1), 1)[0];
    const jitterF = await gen.generateFloat();
    const jitter = (jitterF * 2.0 - 1.0) * FORM_JITTER;
    const horse = roster[pick];
    const augmentMult = await evaluateAugments(commitmentHash, slot, gate, horse, stageId, weatherId, distanceM);
    runners.push({
      horseId: horse.horseId,
      gate,
      weight: deriveWeight(horse, distanceM, stageId, jitter, augmentMult),
      silk,
    });
  }
  return { slot, distanceM, stageId, weatherId, runners };
}
